import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { movies } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

const typeOptions = ["全部", "动画长片", "短片"];
const ratingOptions = [
  { label: "全部评分", value: 0 },
  { label: "8 分以上", value: 8 },
  { label: "7 分以上", value: 7 },
];
const sortOptions = [
  { label: "最新上映", value: "year" },
  { label: "评分最高", value: "rating" },
  { label: "最热门", value: "popularity" },
];

export default function Category() {
  useDocumentMeta(
    "小黄人电影分类 - 动画电影全集，剧情与资讯",
    "小黄人电影全集分类页，收录小黄人系列动画长片与番外短片，支持按年份、类型、评分筛选与排序，快速找到你想看的影片。"
  );
  const [query, setQuery] = useState("");
  const [type, setType] = useState("全部");
  const [year, setYear] = useState("全部");
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState("year");
  const [onlyFav, setOnlyFav] = useState(false);
  const [page, setPage] = useState(1);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { items } = useFavorites();

  const years = useMemo(
    () => ["全部", ...Array.from(new Set(movies.map((m) => String(m.year)))).sort((a, b) => Number(b) - Number(a))],
    []
  );

  const favMovieIds = items.filter((i) => i.type === "movie").map((i) => i.id);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = movies.filter((m) => {
      if (type !== "全部" && m.type !== type) return false;
      if (year !== "全部" && String(m.year) !== year) return false;
      if (m.rating < minRating) return false;
      if (onlyFav && !favMovieIds.includes(m.id)) return false;
      if (
        q &&
        !m.title.toLowerCase().includes(q) &&
        !m.originalTitle.toLowerCase().includes(q) &&
        !m.synopsis.toLowerCase().includes(q) &&
        !m.tags.some((t) => t.toLowerCase().includes(q))
      ) {
        return false;
      }
      return true;
    });

    return list.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "popularity") return b.popularity - a.popularity;
      return b.year - a.year;
    });
  }, [query, type, year, minRating, sort, onlyFav, favMovieIds]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = isMobile
    ? filtered.slice(0, currentPage * PAGE_SIZE)
    : filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setQuery("");
    setType("全部");
    setYear("全部");
    setMinRating(0);
    setSort("year");
    setOnlyFav(false);
    setPage(1);
  };

  const changeFilter = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const pill = (active: boolean) =>
    `whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition cursor-pointer ${
      active
        ? "bg-primary-500 text-foreground-950"
        : "border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
    }`;

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "电影分类" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            小黄人电影系列全片库
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里汇总了小黄人相关的全部动画长片与番外短片。你可以按上映年份、影片类型与评分等级筛选，也可以切换排序方式，快速找到想看的那一部。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="rounded-2xl border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center gap-2 rounded-full bg-background-100 px-4 py-2.5">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={query}
              onChange={(e) => changeFilter(() => setQuery(e.target.value))}
              placeholder="在当前列表内搜索影片…"
              className="w-full min-w-0 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => changeFilter(() => setQuery(""))}
                className="text-foreground-400 hover:text-foreground-700"
                aria-label="清空"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            )}
          </div>

          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-14 shrink-0 text-xs font-medium text-foreground-500">类型</span>
              {typeOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => changeFilter(() => setType(option))}
                  className={pill(type === option)}
                >
                  {option}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-14 shrink-0 text-xs font-medium text-foreground-500">年份</span>
              {years.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => changeFilter(() => setYear(option))}
                  className={pill(year === option)}
                >
                  {option}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-14 shrink-0 text-xs font-medium text-foreground-500">评分</span>
              {ratingOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => changeFilter(() => setMinRating(option.value))}
                  className={pill(minRating === option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-foreground-500">排序</span>
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => changeFilter(() => setSort(option.value))}
                  className={pill(sort === option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => changeFilter(() => setOnlyFav((v) => !v))}
                className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition ${
                  onlyFav
                    ? "bg-secondary-500 text-background-50"
                    : "border border-background-200 text-foreground-600 hover:border-secondary-300"
                }`}
              >
                <i className={onlyFav ? "ri-heart-fill" : "ri-heart-line"}></i>
                只看收藏
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1.5 rounded-full border border-background-200 px-4 py-2 text-sm font-medium text-foreground-600 transition hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-refresh-line"></i>
                重置
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部影片
          </p>
          {onlyFav && (
            <span className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
              <i className="ri-heart-fill"></i>
              正在查看收藏
            </span>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              title="没有符合条件的影片"
              desc="试试调整筛选条件，或者重置后重新浏览全部片库。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
              {visible.map((movie) => (
                <MovieCard key={movie.id} movie={movie} query={query} />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </SiteLayout>
  );
}